"use client";

import { useState, useCallback, useEffect } from "react";
import { IChartApi, ISeriesApi } from "lightweight-charts";
import { registerIndicators } from "../lib/indicators";

interface ChartState {
  chartInstance: IChartApi | null;
  candleSeries: ISeriesApi<"Candlestick"> | null;
  resizeObserver: ResizeObserver | null;
}

interface UseChartStateReturn {
  chartState: ChartState;
  isDebugVisible: boolean;
  isAddTickerVisible: boolean;
  isEditTickerVisible: boolean;
  error: string | null;
  setChartState: (
    state: ChartState | ((prev: ChartState) => ChartState)
  ) => void;
  setError: (error: string | null) => void;
  toggleDebugModal: (show: boolean) => void;
  toggleAddTickerModal: (show: boolean) => void;
  toggleEditTickerModal: (show: boolean) => void;
}

export const useChartState = (): UseChartStateReturn => {
  // Register indicators on first load
  useEffect(() => {
    registerIndicators();
  }, []);

  // Chart state
  const [chartState, setChartState] = useState<ChartState>({
    chartInstance: null,
    candleSeries: null,
    resizeObserver: null,
  });

  // UI state
  const [isDebugVisible, setIsDebugVisible] = useState(false);
  const [isAddTickerVisible, setIsAddTickerVisible] = useState(false);
  const [isEditTickerVisible, setIsEditTickerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal toggles
  const toggleDebugModal = useCallback((show: boolean) => {
    setIsDebugVisible(show);
  }, []);

  const toggleAddTickerModal = useCallback((show: boolean) => {
    setIsAddTickerVisible(show);
  }, []);

  const toggleEditTickerModal = useCallback((show: boolean) => {
    setIsEditTickerVisible(show);
  }, []);

  useEffect(() => {
    if (!error) return;
    const timeoutId = window.setTimeout(() => setError(null), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [error]);

  return {
    chartState,
    isDebugVisible,
    isAddTickerVisible,
    isEditTickerVisible,
    error,
    setChartState,
    setError,
    toggleDebugModal,
    toggleAddTickerModal,
    toggleEditTickerModal,
  };
};
